import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardService from '../services/dashboardService';
import JobMetadataDetails from '../components/JobMetadataDetails';
import './History.css';

const History = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State to track which jobs have expanded details
  const [expandedJobs, setExpandedJobs] = useState({});
  
  useEffect(() => {
    fetchJobsData();
  }, []);
  
  const fetchJobsData = async () => {
    try {
      setLoading(true);
      const response = await DashboardService.getAllClipsGroupedByJob();
      
      if (response.data && response.data.status === 'success') {
        setJobs(response.data.jobs);
      } else {
        setError('Failed to fetch jobs data');
      }
    } catch (err) {
      console.error('Error fetching jobs data:', err);
      setError(err.message || 'An error occurred while fetching jobs');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle expanded details for a job
  const toggleJobDetails = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Extract clip number from key
  const getClipNumber = (objectKey) => {
    if (!objectKey) return 'Unknown';
    
    // Look for patterns like raw_claude_output/{jobId}/2.json
    const match = objectKey.match(/(\d+)\.json$/);
    if (match) {
      return `Clip ${match[1]}`;
    }
    
    return 'Unknown';
  };
  
  // Handle clip click to open/download
  const handleClipClick = (url, filename) => {
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'clip.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Get status badge class
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'status-success';
      case 'processing':
      case 'in progress':
        return 'status-processing';
      case 'failed':
      case 'error':
        return 'status-failed';
      default:
        return 'status-pending';
    }
  };

  return (
    <div className="history-page">
      <h1>Job History</h1>
      
      <button className="refresh-button" onClick={fetchJobsData} disabled={loading}>
        {loading ? 'Loading...' : 'Refresh'}
      </button>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading jobs...</div>
      ) : (
        <div className="jobs-table-container">
          {jobs.length === 0 ? (
            <div className="no-jobs">No jobs available</div>
          ) : (
            <table className="jobs-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Job ID</th>
                  <th>Total Clips</th>
                  <th>Raw Clips</th>
                  <th>Enhanced Clips</th>
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.jobId}>
                    <tr className="job-row">
                      <td>
                        <button 
                          className="expand-button"
                          onClick={() => toggleJobDetails(job.jobId)}
                          aria-label={expandedJobs[job.jobId] ? "Collapse details" : "Expand details"}
                        >
                          {expandedJobs[job.jobId] ? '−' : '+'}
                        </button>
                      </td>
                      <td className="job-id">{job.jobId}</td>
                      <td>{job.clipCount}</td>
                      <td>{job.rawClipCount}</td>
                      <td>{job.enhancedClipCount}</td>
                      <td>{formatDate(job.firstCreatedAt)}</td>
                      <td>{formatDate(job.lastProcessedAt || job.lastCreatedAt)}</td>
                      <td className={`status status-${job.status}`}>{job.status}</td>
                      <td className="actions">
                        <Link to={`/dashboard?videoId=${job.jobId}`} className="view-button">
                          View in Dashboard
                        </Link>
                      </td>
                    </tr>
                    
                    {expandedJobs[job.jobId] && (
                      <tr className="expanded-details">
                        <td colSpan="9">
                          <div className="job-details-wrapper">
                            <JobMetadataDetails 
                              jobId={job.jobId} 
                              videoId={job.jobId}
                              onClose={() => toggleJobDetails(job.jobId)}
                            />
                            
                            <div className="clip-details-container">
                              <div className="clip-section">
                                <h3>Enhanced Clips:</h3>
                                {job.enhancedClips && job.enhancedClips.length > 0 ? (
                                  <div className="clip-list">
                                    {job.enhancedClips.map((clip, index) => (
                                      <div key={index} className="clip-item" onClick={() => clip.downloadUrl && handleClipClick(clip.downloadUrl, `enhanced_${index}.json`)}>
                                        <div className="clip-name">{getClipNumber(clip.objectKey)}</div>
                                        <div className="clip-bucket">{clip.bucket}</div>
                                        <div className="clip-date">{formatDate(clip.enhancedAt || clip.createdAt)}</div>
                                        <button 
                                          className={`download-button ${!clip.downloadUrl ? 'unavailable' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering the parent's onClick
                                            if (clip.downloadUrl) handleClipClick(clip.downloadUrl, `enhanced_${index}.json`);
                                          }}
                                        >
                                          {clip.downloadUrl ? 'Download' : 'Unavailable'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p>No enhanced clips available</p>
                                )}
                              </div>
                              
                              <div className="clip-section">
                                <h3>Raw Clips:</h3>
                                {job.rawClips && job.rawClips.length > 0 ? (
                                  <div className="clip-list">
                                    {job.rawClips.map((clip, index) => (
                                      <div key={index} className="clip-item" onClick={() => clip.downloadUrl && handleClipClick(clip.downloadUrl, `raw_${index}.json`)}>
                                        <div className="clip-name">{getClipNumber(clip.objectKey)}</div>
                                        <div className="clip-bucket">{clip.bucket}</div>
                                        <div className="clip-date">{formatDate(clip.createdAt)}</div>
                                        <button 
                                          className={`download-button ${!clip.downloadUrl ? 'unavailable' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering the parent's onClick
                                            if (clip.downloadUrl) handleClipClick(clip.downloadUrl, `raw_${index}.json`);
                                          }}
                                        >
                                          {clip.downloadUrl ? 'Download' : 'Unavailable'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p>No raw clips available</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default History;